import * as React from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

import LanguageSelector from './LanguageSelector';
import SignInButton from './SignInButton';
import AccountMenu from './AccountMenu.js';
import DashboardNavButton from './DashboardNavButton';

import ResidentNowLogo from "../static/resident-now-logo-transparent.png"

const logoStyle = {
  width: '125px',
  height: 'auto',
  marginLeft: 15,
  cursor: 'pointer',
};

export default function AppAppBar({ userData, language, setLanguage }) {
  const {t} = useTranslation("common");

  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const routeChange = (path) =>{ 
    navigate(path);
  }

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = async (sectionId) => {
    await routeChange('/');
    const sectionElement = document.getElementById(sectionId);
    const offset = 128;
    if (sectionElement) {
      const targetScroll = sectionElement.offsetTop - offset;
      sectionElement.scrollIntoView({ behavior: 'smooth' });
      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth',
      });
      setOpen(false);
    }
  };

  const navigateToPricing = () => {
    if (window.location.pathname === '/') {
      scrollToSection('pricing');
    } 
    else {
      routeChange('/pricing')
    }
  }

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          boxShadow: 0,
          bgcolor: 'transparent',
          backgroundImage: 'none',
          mt: 2,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="regular"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
              borderRadius: '999px',
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(24px)',
              maxHeight: 40,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: `0 0 1px rgba(85, 166, 246, 0.1), 1px 1.5px 2px -1px rgba(85, 166, 246, 0.15), 4px 4px 12px -2.5px rgba(85, 166, 246, 0.15)`
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                ml: '-18px',
                px: 0,
              }}
            >
              <img
                src={
                  ResidentNowLogo 
                }
                style={logoStyle}
                alt="Resident Now logo"
                onClick={() => routeChange('/')}
              />
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <MenuItem
                  onClick={() => scrollToSection('highlights')}
                  sx={{ py: '6px', px: '12px' }}
                >
                  <Typography variant="body2" color="text.primary">
                    {t('appAppBar.highlights')}
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => navigateToPricing()}
                  sx={{ py: '6px', px: '12px' }}
                >
                  <Typography variant="body2" color="text.primary">
                    {t('appAppBar.pricing')}
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => scrollToSection('faq')}
                  sx={{ py: '6px', px: '12px' }}
                >
                  <Typography variant="body2" color="text.primary">
                    {t('appAppBar.faq')}
                  </Typography>
                </MenuItem>
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <LanguageSelector language={language} setLanguage={setLanguage} />
              <Divider orientation="vertical" variant="middle" flexItem />
              <IconButton onClick={() => routeChange('/favorites')}>
                <FavoriteBorderIcon />
              </IconButton> 
              <Divider orientation="vertical" variant="middle" flexItem />
              <DashboardNavButton userData={userData} />
              <SignInButton />
              <AccountMenu userData={userData} />
            </Box>
            <Box sx={{ display: { sm: '', md: 'none' } }}>
              <Button
                variant="text"
                color="primary"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ minWidth: '30px', p: '4px' }}
              >
                <MenuIcon />
              </Button>
              <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
                <Box
                  sx={{
                    minWidth: '60dvw',
                    p: 2,
                    backgroundColor: 'background.paper',
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'end',
                      flexGrow: 1,
                    }}
                  >
                  </Box>
                  <MenuItem onClick={() => scrollToSection('highlights')}>
                    {t('appAppBar.highlights')}
                  </MenuItem>
                  <MenuItem onClick={() => navigateToPricing()}>
                    {t('appAppBar.pricing')}
                  </MenuItem>
                  <MenuItem onClick={() => scrollToSection('faq')}>
                    {t('appAppBar.faq')}
                  </MenuItem>
                  <MenuItem onClick={() => routeChange('/favorites')}>
                    {t('appAppBar.favorites')}
                  </MenuItem>
                  <Divider />
                  <Stack spacing={2}>
                    <LanguageSelector language={language} setLanguage={setLanguage} />
                    <DashboardNavButton userData={userData} />
                    <SignInButton />
                    <AccountMenu userData={userData} />
                  </Stack>
                </Box>
              </Drawer>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </div>
  );
}